import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Lock,
  Pin,
  Send,
  Trash2,
  Pencil,
  X,
  Check,
} from "lucide-react";
import {
  useForumThreadQuery,
  useReplyToForumThreadMutation,
  useUpdateForumThreadMutation,
  useDeleteForumThreadMutation,
  useUpdateForumPostMutation,
  useDeleteForumPostMutation,
  usePinForumThreadMutation,
  useLockForumThreadMutation,
  type ForumPerson,
} from "../api/forumApi";
import { AttachmentList, AttachmentPicker } from "../components/Attachments";

function when(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function initials(name?: string) {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

function Byline({ person, at }: { person: ForumPerson | null; at: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-2xl bg-gray-900 text-white flex items-center justify-center text-[10px] font-black shrink-0">
        {initials(person?.name)}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-black text-gray-900 truncate">
          {person?.name ?? "Unknown"}
        </div>
        <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 flex-wrap">
          {person?.seat && (
            <span className="rounded-lg bg-gray-100 px-2 py-0.5 text-gray-500">
              {person.seat}
            </span>
          )}
          <span>{when(at)}</span>
        </div>
      </div>
    </div>
  );
}

/** Plain text, line breaks kept. Never HTML — see NewThreadPage. */
function Body({ text }: { text: string }) {
  return (
    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap mt-3">
      {text}
    </p>
  );
}

export default function ForumThreadPage() {
  const { id } = useParams();
  const threadId = Number(id);
  const navigate = useNavigate();

  const { data: thread, isLoading, isError, error } = useForumThreadQuery(threadId);

  const [reply, { isLoading: replying }] = useReplyToForumThreadMutation();
  const [updateThread, { isLoading: savingThread }] = useUpdateForumThreadMutation();
  const [deleteThread] = useDeleteForumThreadMutation();
  const [updatePost] = useUpdateForumPostMutation();
  const [deletePost] = useDeleteForumPostMutation();
  const [pin] = usePinForumThreadMutation();
  const [lock] = useLockForumThreadMutation();

  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [problem, setProblem] = useState<string | null>(null);

  const [editingThread, setEditingThread] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftBody, setDraftBody] = useState("");

  const [editingPost, setEditingPost] = useState<number | null>(null);
  const [draftPost, setDraftPost] = useState("");

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-300">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (isError || !thread) {
    const status = (error as any)?.status;
    return (
      <div className="max-w-lg mx-auto text-center py-24 px-6">
        <p className="text-sm font-bold text-gray-600">
          {status === 403
            ? "This discussion is open to the Policy Committee only."
            : "That discussion could not be opened."}
        </p>
        <button
          type="button"
          onClick={() => navigate("/forum")}
          className="mt-4 text-[10px] font-black uppercase tracking-[0.15em] text-gray-400 hover:text-gray-600"
        >
          Back to the forum
        </button>
      </div>
    );
  }

  const say = async (fn: () => Promise<unknown>, fallback: string) => {
    setProblem(null);
    try {
      await fn();
    } catch (e: any) {
      setProblem(e?.data?.message || e?.error || fallback);
    }
  };

  const postReply = () => {
    if (!body.trim()) {
      setProblem("Write something first.");
      return;
    }

    return say(async () => {
      await reply({ threadId, body: body.trim(), files }).unwrap();
      setBody("");
      setFiles([]);
    }, "The reply could not be posted.");
  };

  const saveThread = () =>
    say(async () => {
      await updateThread({
        id: threadId,
        title: draftTitle.trim(),
        body: draftBody.trim(),
      }).unwrap();
      setEditingThread(false);
    }, "The discussion could not be saved.");

  const withdrawThread = () =>
    say(async () => {
      if (!window.confirm("Withdraw this discussion and all its replies?")) return;
      await deleteThread(threadId).unwrap();
      navigate("/forum");
    }, "The discussion could not be withdrawn.");

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <button
        type="button"
        onClick={() => navigate("/forum")}
        className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.15em] text-gray-400 hover:text-gray-600 mb-5"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to the forum
      </button>

      {/* ── Opening post ────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              {thread.is_pinned && <Pin className="w-4 h-4 text-amber-500" />}
              {thread.is_locked && <Lock className="w-4 h-4 text-gray-400" />}
              {editingThread ? (
                <input
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                  className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm font-black focus:outline-none focus:border-gray-400"
                />
              ) : (
                <h1 className="text-lg font-black text-gray-900 tracking-tight">
                  {thread.title}
                </h1>
              )}
            </div>
            <Byline person={thread.author} at={thread.created_at} />
          </div>

          {/* Author's own controls, plus the secretariat's housekeeping. */}
          <div className="flex items-center gap-1.5 shrink-0">
            {thread.can_moderate && (
              <>
                <button
                  type="button"
                  title={thread.is_pinned ? "Unpin" : "Pin to the top"}
                  onClick={() => say(() => pin({ id: threadId, pinned: !thread.is_pinned }).unwrap(), "Could not pin.")}
                  className={`rounded-xl border p-2 ${
                    thread.is_pinned
                      ? "border-amber-200 bg-amber-50 text-amber-600"
                      : "border-gray-200 text-gray-400 hover:border-gray-300"
                  }`}
                >
                  <Pin className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  title={thread.is_locked ? "Unlock" : "Lock — no further replies"}
                  onClick={() => say(() => lock({ id: threadId, locked: !thread.is_locked }).unwrap(), "Could not lock.")}
                  className={`rounded-xl border p-2 ${
                    thread.is_locked
                      ? "border-gray-300 bg-gray-100 text-gray-600"
                      : "border-gray-200 text-gray-400 hover:border-gray-300"
                  }`}
                >
                  <Lock className="w-3.5 h-3.5" />
                </button>
              </>
            )}
            {thread.can_edit && !editingThread && (
              <>
                <button
                  type="button"
                  title="Edit"
                  onClick={() => {
                    setDraftTitle(thread.title);
                    setDraftBody(thread.body);
                    setEditingThread(true);
                  }}
                  className="rounded-xl border border-gray-200 p-2 text-gray-400 hover:border-gray-300"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  title="Withdraw"
                  onClick={withdrawThread}
                  className="rounded-xl border border-gray-200 p-2 text-gray-400 hover:border-rose-200 hover:text-rose-600"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        </div>

        {editingThread ? (
          <div className="mt-4 space-y-3">
            <textarea
              value={draftBody}
              onChange={(e) => setDraftBody(e.target.value)}
              rows={10}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm leading-relaxed focus:outline-none focus:border-gray-400"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditingThread(false)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-[10px] font-black uppercase tracking-[0.15em] text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
                Cancel
              </button>
              <button
                type="button"
                onClick={saveThread}
                disabled={savingThread}
                className="inline-flex items-center gap-1.5 rounded-xl bg-gray-900 px-4 py-2 text-[10px] font-black uppercase tracking-[0.15em] text-white disabled:opacity-60"
              >
                <Check className="w-3.5 h-3.5" />
                Save
              </button>
            </div>
          </div>
        ) : (
          <>
            <Body text={thread.body} />
            <AttachmentList items={thread.attachments} />
          </>
        )}
      </div>

      {/* ── Replies ─────────────────────────────────────────────────── */}
      <div className="mt-5 space-y-3">
        {thread.posts.map((post) => {
          // The line between what the reader had seen and what arrived since.
          const isNew =
            !!thread.last_read_at &&
            new Date(post.created_at).getTime() > new Date(thread.last_read_at).getTime();

          return (
            <div key={post.id}>
              {isNew && (
                <div className="flex items-center gap-3 my-4">
                  <div className="h-px flex-1 bg-indigo-200" />
                  <span className="text-[9px] font-black uppercase tracking-[0.15em] text-indigo-500">
                    New since your last visit
                  </span>
                  <div className="h-px flex-1 bg-indigo-200" />
                </div>
              )}

              <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <Byline person={post.author} at={post.created_at} />
                  <div className="flex items-center gap-1.5 shrink-0">
                    {post.can_edit && editingPost !== post.id && (
                      <button
                        type="button"
                        title="Edit"
                        onClick={() => {
                          setEditingPost(post.id);
                          setDraftPost(post.body);
                        }}
                        className="rounded-xl border border-gray-200 p-2 text-gray-400 hover:border-gray-300"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {post.can_delete && (
                      <button
                        type="button"
                        title="Withdraw"
                        onClick={() =>
                          say(async () => {
                            if (!window.confirm("Withdraw this reply?")) return;
                            await deletePost({ id: post.id, threadId }).unwrap();
                          }, "The reply could not be withdrawn.")
                        }
                        className="rounded-xl border border-gray-200 p-2 text-gray-400 hover:border-rose-200 hover:text-rose-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {editingPost === post.id ? (
                  <div className="mt-3 space-y-3">
                    <textarea
                      value={draftPost}
                      onChange={(e) => setDraftPost(e.target.value)}
                      rows={6}
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm leading-relaxed focus:outline-none focus:border-gray-400"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingPost(null)}
                        className="rounded-xl border border-gray-200 px-3 py-2 text-[10px] font-black uppercase tracking-[0.15em] text-gray-600"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          say(async () => {
                            await updatePost({
                              id: post.id,
                              threadId,
                              body: draftPost.trim(),
                            }).unwrap();
                            setEditingPost(null);
                          }, "The reply could not be saved.")
                        }
                        className="rounded-xl bg-gray-900 px-4 py-2 text-[10px] font-black uppercase tracking-[0.15em] text-white"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <Body text={post.body} />
                    <AttachmentList items={post.attachments} />
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {problem && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 mt-4">
          <p className="text-xs font-bold text-rose-700">{problem}</p>
        </div>
      )}

      {/* ── Reply box ───────────────────────────────────────────────── */}
      {thread.is_locked ? (
        <div className="rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4 mt-5 flex items-center gap-3">
          <Lock className="w-4 h-4 text-gray-400" />
          <p className="text-xs font-bold text-gray-500">
            This discussion is closed. No further replies can be added.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm mt-5 space-y-4">
          <label className="block text-[10px] font-black uppercase tracking-[0.15em] text-gray-500">
            Your reply
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={replying}
            rows={5}
            placeholder="Add to the discussion…"
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm leading-relaxed focus:outline-none focus:border-gray-400"
          />
          <AttachmentPicker files={files} onChange={setFiles} disabled={replying} />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={postReply}
              disabled={replying}
              className="inline-flex items-center gap-2 rounded-xl bg-gray-900 px-5 py-2.5 text-[10px] font-black uppercase tracking-[0.15em] text-white hover:bg-gray-800 disabled:opacity-60"
            >
              {replying ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5" />
              )}
              Post reply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
